import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Crosshair, Satellite } from 'lucide-react';
import { Field, QueryState, Select, formatDate, todayIso, useFormErrors } from '@/components/live';
import { RecordDrawer } from '@/components/RecordDrawer';
import { DropZone } from '@/components/DropZone';
import { DeleteRecordButton } from '@/components/DeleteRecordButton';
import { callRpc, createRow, saveRow } from '@/api/resources';
import { signedUrlsFor, uploadDocument } from '@/api/documents';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/lib/toast';
import { acquisitionAt, centroidOf, outerRings, renderSnapshot, waybackReleases, type WaybackRelease } from '@/lib/wayback';
import type { HarvestRow } from '@/types/records';

interface EvidenceRow {
  id: string; evidence_code: string | null; stage: 'before' | 'after'; capture_kind: string; imagery_source: string | null;
  imagery_release: string | null; image_date: string; document_id: string; latitude: number | null; longitude: number | null; remarks: string | null;
}

const KIND_LABEL: Record<string, string> = { satellite: 'Satellite', ground_photo: 'Ground photo', drone: 'Drone photo' };

/**
 * Before-and-after images that confirm a harvest (owner, 2026-09-21: "before and after satellite
 * image photo capture system which confirm the harvest of plot"). A satellite image is captured over
 * the plot boundary from a dated imagery release and recorded with the date the image was actually
 * taken; a ground or drone photo is uploaded with where it was taken. The database refuses a
 * "before" image dated after the harvest, or an "after" image dated before it.
 */
export function HarvestEvidence({ harvest, onClose }: { harvest: HarvestRow; onClose: () => void }) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const mayAdd = can(profile, 'plot.harvest', 'create');
  const rows = useRows<EvidenceRow>('harvest_evidence_images', { filters: { harvest_id: harvest.id }, orderBy: { column: 'image_date' } });
  const ids = (rows.data ?? []).map((row) => row.document_id);
  const links = useQuery({ queryKey: ['signed-urls', ...ids], queryFn: () => signedUrlsFor(ids), enabled: ids.length > 0 });
  const geometry = useQuery({
    queryKey: ['plot-geometry', harvest.plot_id],
    queryFn: () => callRpc<{ geometry?: { type: string; coordinates: unknown } } | null>('fn_plot_geojson', { p_plot_id: harvest.plot_id }),
    enabled: isSupabaseConfigured,
  });
  const rings = useMemo(() => outerRings(geometry.data?.geometry), [geometry.data]);
  const [mode, setMode] = useState<null | 'satellite' | 'photo'>(null);
  const before = (rows.data ?? []).filter((row) => row.stage === 'before');
  const after = (rows.data ?? []).filter((row) => row.stage === 'after');

  function done() {
    setMode(null);
    void queryClient.invalidateQueries({ queryKey: ['harvest_evidence_images'] });
    void queryClient.invalidateQueries({ queryKey: ['plot_harvest_entries'] });
  }

  return (
    <RecordDrawer
      record={{ table: 'ops.plot_harvest_entries', id: harvest.id, invalidate: ['plot_harvest_entries'] }}
      footer={<button onClick={onClose} type="button">Close</button>}
      onClose={onClose}
      open
      subtitle={`${harvest.plot_code ?? ''} · ${harvest.species_label ?? ''} · harvested ${formatDate(harvest.harvest_date)}`}
      title={`Harvest evidence ${harvest.harvest_code ?? ''}`}
      wide
    >
      <p className="muted" style={{ marginTop: 0 }}>
        {before.length > 0 && after.length > 0
          ? 'Before and after images are on file: the harvest is evidenced.'
          : `Needs ${before.length === 0 ? 'a "before"' : ''}${before.length === 0 && after.length === 0 ? ' and ' : ''}${after.length === 0 ? 'an "after"' : ''} image.`}
      </p>
      {mayAdd && !mode && (
        <div className="toolbar-row">
          <button disabled={rings.length === 0} onClick={() => setMode('satellite')}
                  title={rings.length === 0 ? 'Draw the plot boundary first.' : undefined} type="button">
            <Satellite size={15} /> Capture satellite image
          </button>
          <button onClick={() => setMode('photo')} type="button"><Camera size={15} /> Upload ground / drone photo</button>
        </div>
      )}
      {mode === 'satellite' && profile?.organization && (
        <SatelliteCapture harvest={harvest} onCancel={() => setMode(null)} onSaved={done} organizationId={profile.organization.id} rings={rings} />
      )}
      {mode === 'photo' && profile?.organization && (
        <PhotoUpload harvest={harvest} onCancel={() => setMode(null)} onSaved={done} organizationId={profile.organization.id} />
      )}

      <QueryState empty="No evidence yet." query={rows}>
        <div className="evidence-pair">
          {(['before', 'after'] as const).map((stage) => (
            <div className="evidence-column" key={stage}>
              <h3>{stage === 'before' ? 'Before the harvest' : 'After the harvest'}</h3>
              {(stage === 'before' ? before : after).map((row) => (
                <figure className="evidence-card" key={row.id}>
                  {links.data?.[row.document_id]
                    ? <a href={links.data[row.document_id]} rel="noreferrer" target="_blank"><img alt={`${stage} ${row.image_date}`} src={links.data[row.document_id]} /></a>
                    : <div className="evidence-placeholder">Loading...</div>}
                  <figcaption>
                    <strong>{KIND_LABEL[row.capture_kind] ?? row.capture_kind} · {formatDate(row.image_date)}</strong>
                    <small className="muted">{[row.imagery_release, row.latitude != null ? `${row.latitude}, ${row.longitude}` : null, row.remarks, row.evidence_code].filter(Boolean).join(' · ')}</small>
                  </figcaption>
                  <DeleteRecordButton
                    deleteFn={(reason) => saveRow('harvest_evidence_images', row.id, { is_deleted: true, delete_reason: reason })}
                    entity="feature" id={row.id} invalidate={['harvest_evidence_images', 'plot_harvest_entries']}
                    label={`${stage} image`} onDeleted={() => undefined} screenKey="plot.harvest" />
                </figure>
              ))}
              {(stage === 'before' ? before : after).length === 0 && <p className="muted">None yet.</p>}
            </div>
          ))}
        </div>
      </QueryState>
    </RecordDrawer>
  );
}

function SatelliteCapture({ harvest, rings, organizationId, onCancel, onSaved }: {
  harvest: HarvestRow; rings: Array<Array<[number, number]>>; organizationId: string; onCancel: () => void; onSaved: () => void;
}) {
  const releases = useQuery({ queryKey: ['wayback-releases'], queryFn: waybackReleases, staleTime: 24 * 3600 * 1000 });
  const [stage, setStage] = useState<'before' | 'after'>('before');
  const [releaseNum, setReleaseNum] = useState('');
  const [preview, setPreview] = useState<{ blob: Blob; url: string; imageDate: string; release: WaybackRelease; source: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const centre = centroidOf(rings);

  // Releases around the harvest: for "before", published up to 60 days after it (the imagery in a
  // release is older than the release); for "after", published after the harvest.
  const options = (releases.data ?? [])
    .filter((release) => (stage === 'before'
      ? release.releaseDate <= addDays(harvest.harvest_date, 60)
      : release.releaseDate >= harvest.harvest_date))
    .map((release) => ({ id: String(release.releaseNum), label: `Release ${release.releaseDate} (${release.identifier})` }));

  async function capture() {
    const release = releases.data?.find((row) => String(row.releaseNum) === releaseNum);
    if (!release || !centre) return;
    setBusy(true);
    setError(undefined);
    try {
      const acquired = await acquisitionAt(release, centre[0], centre[1]);
      const imageDate = acquired?.date ?? release.releaseDate;
      const blob = await renderSnapshot(release, rings, [
        `${harvest.plot_code ?? 'Plot'} - ${stage === 'before' ? 'BEFORE' : 'AFTER'} harvest of ${formatDate(harvest.harvest_date)}`,
        `Image taken ${formatDate(imageDate)}${acquired?.source ? ` (${acquired.source}${acquired.resolutionM ? `, ${acquired.resolutionM} m` : ''})` : ' (release date; acquisition date not published)'}`,
        `Esri World Imagery Wayback ${release.identifier}, released ${formatDate(release.releaseDate)} · captured ${formatDate(todayIso())}`,
      ]);
      setPreview((old) => { if (old) URL.revokeObjectURL(old.url); return { blob, url: URL.createObjectURL(blob), imageDate, release, source: acquired?.source ?? null }; });
      if (stage === 'before' && imageDate > harvest.harvest_date) setError(`That imagery was taken ${formatDate(imageDate)}, after the harvest. Choose an older release.`);
      if (stage === 'after' && imageDate < harvest.harvest_date) setError(`That imagery was taken ${formatDate(imageDate)}, before the harvest - it does not show the result yet. Choose a newer release.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The image could not be captured.');
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!preview) return;
    setBusy(true);
    try {
      const file = new File([preview.blob], `${harvest.harvest_code ?? 'harvest'}-${stage}-${preview.release.identifier}.png`, { type: 'image/png' });
      const document = await uploadDocument({ file, bucket: 'plot-evidence', organizationId, ownerId: harvest.plot_id, captureMethod: 'import', issuedDate: preview.imageDate });
      await createRow('harvest_evidence_images', {
        harvest_id: harvest.id, stage, capture_kind: 'satellite', imagery_source: 'esri_wayback',
        imagery_release: `${preview.release.identifier} (released ${preview.release.releaseDate})${preview.source ? ` - ${preview.source}` : ''}`,
        image_date: preview.imageDate, document_id: document.id,
        latitude: centre ? Number(centre[1].toFixed(6)) : null, longitude: centre ? Number(centre[0].toFixed(6)) : null,
      });
      toast.success(`${stage === 'before' ? 'Before' : 'After'} image saved.`);
      onSaved();
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'That could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="inline-form">
      <div className="field-row">
        <Field label="Stage">
          <Select onChange={(value) => { setStage((value || 'before') as 'before' | 'after'); setReleaseNum(''); setPreview(null); setError(undefined); }}
                  options={[{ id: 'before', label: 'Before the harvest' }, { id: 'after', label: 'After the harvest' }]} value={stage} />
        </Field>
        <Field hint="Esri World Imagery archive. The date the image was taken is read for this plot." label="Imagery release">
          <Select onChange={(value) => { setReleaseNum(value); setPreview(null); setError(undefined); }} options={options}
                  placeholder={releases.isLoading ? 'Loading the archive...' : releases.isError ? 'The archive could not be reached' : options.length ? 'Select a release' : 'No release on that side of the harvest yet'}
                  value={releaseNum} />
        </Field>
      </div>
      {preview && <img alt="Captured imagery" className="evidence-preview" src={preview.url} />}
      {error && <p className="field-error">{error}</p>}
      <div className="inline-form-actions">
        <button className="cancel" onClick={onCancel} type="button">Cancel</button>
        <button disabled={!releaseNum || busy} onClick={() => void capture()} type="button"><Satellite size={15} /> {busy && !preview ? 'Capturing...' : preview ? 'Capture again' : 'Capture'}</button>
        <button className="primary" disabled={!preview || busy || Boolean(error)} onClick={() => void save()} type="button">Save as {stage} image</button>
      </div>
    </div>
  );
}

function PhotoUpload({ harvest, organizationId, onCancel, onSaved }: {
  harvest: HarvestRow; organizationId: string; onCancel: () => void; onSaved: () => void;
}) {
  const { errors, check } = useFormErrors();
  const [form, setForm] = useState({ stage: 'after', capture_kind: 'ground_photo', image_date: todayIso(), latitude: '', longitude: '', remarks: '' });
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  function locate() {
    navigator.geolocation?.getCurrentPosition(
      (position) => setForm((now) => ({ ...now, latitude: position.coords.latitude.toFixed(6), longitude: position.coords.longitude.toFixed(6) })),
      () => toast.error('The location could not be read.'),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  }

  async function save() {
    if (!check({
      document: file ? undefined : 'Choose the photograph.',
      image_date: !form.image_date ? 'When was it taken?'
        : form.stage === 'before' && form.image_date > harvest.harvest_date ? 'A "before" photo must be from on or before the harvest date.'
          : form.stage === 'after' && form.image_date < harvest.harvest_date ? 'An "after" photo must be from on or after the harvest date.'
            : form.image_date > todayIso() ? 'Cannot be in the future.' : undefined,
      latitude: form.latitude && (Number.isNaN(Number(form.latitude)) || Math.abs(Number(form.latitude)) > 90) ? 'Check the latitude.' : undefined,
      longitude: form.longitude && (Number.isNaN(Number(form.longitude)) || Math.abs(Number(form.longitude)) > 180) ? 'Check the longitude.' : undefined,
    }) || !file) return;
    setBusy(true);
    try {
      const document = await uploadDocument({ file, bucket: 'plot-evidence', organizationId, ownerId: harvest.plot_id, captureMethod: 'camera', issuedDate: form.image_date });
      await createRow('harvest_evidence_images', {
        harvest_id: harvest.id, stage: form.stage, capture_kind: form.capture_kind, image_date: form.image_date, document_id: document.id,
        latitude: form.latitude ? Number(form.latitude) : null, longitude: form.longitude ? Number(form.longitude) : null,
        remarks: form.remarks.trim() || null,
      });
      toast.success('Photo saved.');
      onSaved();
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'That could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="inline-form">
      <div className="field-row">
        <Field label="Stage">
          <Select onChange={(value) => setForm({ ...form, stage: value || 'after' })} options={[{ id: 'before', label: 'Before the harvest' }, { id: 'after', label: 'After the harvest' }]} value={form.stage} />
        </Field>
        <Field label="Kind">
          <Select onChange={(value) => setForm({ ...form, capture_kind: value || 'ground_photo' })} options={[{ id: 'ground_photo', label: 'Ground photo' }, { id: 'drone', label: 'Drone photo' }]} value={form.capture_kind} />
        </Field>
      </div>
      <div className="field-row">
        <Field error={errors.image_date} label="Taken on" required>
          <input max={todayIso()} onChange={(event) => setForm({ ...form, image_date: event.target.value })} type="date" value={form.image_date} />
        </Field>
        <Field label="Where taken">
          <button onClick={locate} type="button"><Crosshair size={15} /> Use my location</button>
        </Field>
      </div>
      <div className="field-row">
        <Field error={errors.latitude} label="Latitude"><input inputMode="decimal" onChange={(event) => setForm({ ...form, latitude: event.target.value })} value={form.latitude} /></Field>
        <Field error={errors.longitude} label="Longitude"><input inputMode="decimal" onChange={(event) => setForm({ ...form, longitude: event.target.value })} value={form.longitude} /></Field>
      </div>
      <Field error={errors.document} hint={file ? `${file.name} will be uploaded.` : undefined} label="Photograph" required>
        <DropZone accept="image/*" buttonLabel={file ? 'Change photo' : 'Choose photo'} label="Drag the photo here, or" onFile={setFile} />
      </Field>
      <Field label="Remarks"><input onChange={(event) => setForm({ ...form, remarks: event.target.value })} placeholder="e.g. facing north from the east corner" value={form.remarks} /></Field>
      <div className="inline-form-actions">
        <button className="cancel" onClick={onCancel} type="button">Cancel</button>
        <button className="primary" disabled={busy} onClick={() => void save()} type="button">{busy ? 'Saving...' : 'Save photo'}</button>
      </div>
    </div>
  );
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
